/**
 * Tooltip — accessible hover/focus tooltip built on Radix UI primitives.
 *
 * Wraps `@radix-ui/react-tooltip` with CSK-token styling. Keyboard-,
 * touch-, and screen-reader-friendly by construction: the trigger gets
 * `aria-describedby` pointing at the content, focus restoration is
 * handled by Radix, and rapid open/close is debounced through the
 * `TooltipProvider`'s delay duration.
 *
 * Usage:
 *
 *     <TooltipProvider delayDuration={250}>
 *       <Tooltip>
 *         <TooltipTrigger asChild>
 *           <button type="button">…</button>
 *         </TooltipTrigger>
 *         <TooltipContent>Full explanation.</TooltipContent>
 *       </Tooltip>
 *     </TooltipProvider>
 *
 * `asChild` lets the trigger merge its props into an existing element
 * instead of rendering a wrapper button — useful when the trigger is
 * already an interactive element.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "../../styling/index.js";

export const TooltipProvider = TooltipPrimitive.Provider;
export const Tooltip = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export type TooltipContentProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>;

export const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(function TooltipContent({ className, sideOffset = 4, children, ...props }, ref) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-50 max-w-xs rounded-md px-3 py-1.5 text-xs leading-snug shadow-md",
          "bg-neutral-900 text-white",
          "animate-in fade-in-0 zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="fill-neutral-900" width={10} height={5} />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
});
